import { init, browserTracingIntegration, ErrorEvent } from '@sentry/react'
import { SpanJSON } from '@sentry/core'

import { getLoggedUser, isSignedIn } from '../userAuth'

const getAccountTagInfo = () => {
  if (isSignedIn()) {
    const user = getLoggedUser()
    return user?.email
  }
  return null
}

declare global {
  interface Navigator {
    deviceMemory?: number
    connection?: {
      effectiveType?: string
      rtt?: number
      downlink?: number
      upload?: number
      type?: string
    }
  }
}

const addCustomTags = (currentTags: ErrorEvent['tags']) => {
  const userAgent = {
    deviceMemory: navigator?.deviceMemory,
    connection: navigator?.connection?.effectiveType,
    downlink: navigator?.connection?.downlink,
  }
  return {
    ...currentTags,
    account: getAccountTagInfo(),
    ...userAgent,
  }
}

type BeforeSendTransactionEvent = Parameters<
  typeof init
>[0]['beforeSendTransaction']

export const beforeSendTransaction: BeforeSendTransactionEvent = (event) => {
  const updatedTags = addCustomTags(event.tags)

  const updatedEvent = {
    ...event,
    tags: updatedTags,
  }

  const skipSpanFilter = process.env.REACT_APP_SENTRY_SKIP_SPAN_FILTER
    ? process.env.REACT_APP_SENTRY_SKIP_SPAN_FILTER.split(',')
    : []

  if (skipSpanFilter.length === 0) {
    return updatedEvent
  }

  const filteredSpans =
    updatedEvent?.spans?.filter((span: SpanJSON) => {
      const spanHost = String(span?.data?.['server.address'] || '')
      return !skipSpanFilter.some((substring) => spanHost?.includes(substring))
    }) ?? []

  if (filteredSpans.length !== updatedEvent?.spans?.length) {
    return { ...updatedEvent, spans: filteredSpans }
  }
  return updatedEvent
}

export const beforeSend = (event: ErrorEvent) => {
  const updatedTags = addCustomTags(event.tags)
  const updatedEvent = {
    ...event,
    tags: updatedTags,
  }

  return updatedEvent
}

export const initSentry = () => {
  init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_STAGE,
    enabled: process.env.REACT_APP_ENABLE_SENTRY === 'true',
    beforeSend,
    beforeSendTransaction,
    integrations: [browserTracingIntegration()],
    tracesSampleRate: Number(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.4',
    ),
    tracePropagationTargets: (
      process.env.REACT_APP_SENTRY_TRACE_PROPAGATION_TARGETS || ''
    ).split(','),
  })
}

initSentry()
